/**
 * The grid's read-only view of the catalog: a SKU row shows the NetSuite item's name and pack size
 * and a customer row shows where it comes from, whatever the row was imported as. Everything here
 * runs on the render copy, never on the saved data, so an overlay can neither dirty a plan nor be
 * written back to it.
 */
import type { CatalogOverlay, CustomerOverlay, SkuOverlay } from "../api/forecast_catalog_api"
import { aggregateChildrenToParents } from "./aggregation"
import { node_units_per_case } from "./quantity_basis"

interface OverlayNode {
  name: string
  stable_key?: string | null
  metadata?: Record<string, unknown> | null
  children?: OverlayNode[]
  sales_data?: Array<{ metrics?: { cases_sold?: number | null; units_sold?: number | null } | null }>
}

/** An opportunity row repeats its SKU's key with a suffix; it is the same product. */
function sku_key(stable_key: string): string {
  return stable_key.split(":opportunity:")[0]
}

/** Lay the catalog's facts over a tree: new nodes where something changes, the same ones elsewhere. */
export function apply_catalog_overlay<T extends OverlayNode>(roots: T[], overlay: CatalogOverlay | null | undefined): T[] {
  if (!overlay) return roots
  const skus = new Map<string, SkuOverlay>(overlay.skus.map((sku: SkuOverlay) => [sku.stable_key, sku]))
  const customers = new Map<string, CustomerOverlay>(overlay.customers.map((c: CustomerOverlay) => [c.stable_key, c]))

  function visit(node: T): T {
    const key = node.stable_key ?? ""
    const customer = customers.get(key)
    const sku = skus.get(sku_key(key))
    const children = (node.children as T[] | undefined)?.map(visit)
    const children_changed = children?.some((child: T, index: number) => child !== node.children?.[index]) ?? false
    if (!customer && !sku && !children_changed) return node
    const next: T = { ...node, children: children ?? node.children }
    if (customer) {
      next.metadata = { ...(node.metadata ?? {}), customer_source: customer }
    }
    if (sku) {
      next.name = sku.name
      next.metadata = {
        ...(node.metadata ?? {}),
        product_sku: sku.netsuite_item_id ?? node.metadata?.product_sku,
        units_per_case: node.metadata?.units_per_case ?? sku.units_per_case ?? undefined,
        packs_per_case: sku.packs_per_case ?? undefined,
        catalog_item: true,
      }
    }
    return next
  }

  return roots.map(visit)
}

export type PackBasis = "inner_pack" | "unit" | "case"

export interface PackFactor {
  /** What one case counts as in Packs view. */
  per_case: number
  basis: PackBasis
}

function positive(value: unknown): number | null {
  const n = Number(value)
  return value !== undefined && value !== null && Number.isFinite(n) && n > 0 ? n : null
}

/** Selling units in a case; Packs view keeps the item's own under `case_units`. */
function case_units(node: { metadata?: Record<string, unknown> | null } | null | undefined): number | null {
  return positive(node?.metadata?.case_units) ?? node_units_per_case(node)
}

/**
 * What a "pack" is for one product. Meat sticks go case → pack → unit: 6 caddies of 15 in a case
 * of 90. A supplement goes case → unit, so its pack is the unit. A product NetSuite gives no pack
 * size stays in cases: it is counted, not guessed.
 */
export function pack_factor(node: { metadata?: Record<string, unknown> | null } | null | undefined): PackFactor {
  const packs = positive(node?.metadata?.packs_per_case)
  const units = case_units(node)
  if (packs && packs > 1) {
    return { per_case: packs, basis: units && units > packs ? "inner_pack" : "unit" }
  }
  if (units && units > 1) return { per_case: units, basis: "unit" }
  return { per_case: 1, basis: "case" }
}

/** The conversion a SKU row prints, so nobody has to ask which unit a number is in. */
export function pack_caption(node: { metadata?: Record<string, unknown> | null } | null | undefined): string {
  const units = case_units(node)
  const { per_case, basis } = pack_factor(node)
  if (basis === "inner_pack") return `1 case = ${per_case} packs = ${units} units`
  if (basis === "unit") return `1 case = ${per_case} units, no inner pack`
  return "No pack size in NetSuite, counted in cases"
}

/** The Packs toggle's hover text: the three rules, in order. */
export const PACKS_VIEW_HINT =
  "Packs: cases × the item's inner packs (Case Quantity in NetSuite). A product with no inner pack shows its units. A product with no pack size stays in cases, and its row says so."

/**
 * Packs view: each leaf's `units_sold` becomes `cases × packs per case` and its pack size becomes
 * that factor, so the Units cell code shows packs and an edit divides back to cases. Parents
 * re-roll from the leaves.
 */
export function packs_view_of_hierarchy<T extends OverlayNode>(roots: T[]): T[] {
  function visit(node: T): T {
    const children = node.children as T[] | undefined
    if (Array.isArray(children) && children.length > 0) {
      return { ...node, children: children.map(visit) }
    }
    const { per_case } = pack_factor(node)
    return {
      ...node,
      metadata: { ...(node.metadata ?? {}), units_per_case: per_case, case_units: case_units(node) },
      sales_data: (node.sales_data ?? []).map((point) => ({
        ...point,
        metrics: { ...(point.metrics ?? {}), units_sold: Math.round(Number(point.metrics?.cases_sold ?? 0) * per_case) },
      })),
    }
  }
  return aggregateChildrenToParents(roots.map(visit) as any[]) as T[]
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function leaf(stable_key: string, metadata: Record<string, unknown> | null, cases = 2) {
    return {
      id: stable_key,
      name: "Level-1 Powder (30srv): Ice Cream Cookie (SP)",
      level: "sku",
      stable_key,
      metadata,
      children: [],
      sales_data: [
        {
          period: { period_key: "2027-01", year: 2027, month: 1 },
          metrics: { store_count: 10, volume_dollars: 100, cases_sold: cases, case_cost: 10 },
          is_forecast: true,
        },
      ],
    }
  }

  const overlay: CatalogOverlay = {
    skus: [
      {
        stable_key: "sku:level_1_ice_cream",
        product_id: "p1",
        netsuite_item_id: "16396",
        name: "Level-1-Ice Cream Cookie-855065",
        units_per_case: 6,
        packs_per_case: 6,
      },
    ],
    customers: [{ stable_key: "target_direct", name: "Target @ Direct", source: "netsuite", netsuite_id: "61904", netsuite_name: "31256 Target" }],
  }

  describe("apply_catalog_overlay", () => {
    it("names a row after the NetSuite item and marks its customer, on a copy", () => {
      const sku = leaf("sku:level_1_ice_cream", null)
      const root = { id: "c", name: "Target @ Direct", level: "customer", stable_key: "target_direct", metadata: null, children: [sku], sales_data: [] }
      const out = apply_catalog_overlay([root], overlay) as any
      expect(out[0].metadata.customer_source.source).toBe("netsuite")
      expect(out[0].children[0].name).toBe("Level-1-Ice Cream Cookie-855065")
      expect(out[0].children[0].metadata.product_sku).toBe("16396")
      expect(sku.name).toContain("(SP)")
      expect(root.metadata).toBeNull()
    })

    it("reads an opportunity row as its product, and leaves unknown rows the same object", () => {
      const out = apply_catalog_overlay([leaf("sku:level_1_ice_cream:opportunity:opp_1", null)], overlay) as any
      expect(out[0].name).toBe("Level-1-Ice Cream Cookie-855065")
      const unknown = [leaf("sku:other", null)]
      expect(apply_catalog_overlay(unknown, overlay)[0]).toBe(unknown[0])
      expect(apply_catalog_overlay(unknown, null)).toBe(unknown)
    })
  })

  describe("pack_factor", () => {
    it("reads case → pack → unit, case → unit, and no pack size", () => {
      expect(pack_factor({ metadata: { packs_per_case: 6, units_per_case: 90 } })).toEqual({ per_case: 6, basis: "inner_pack" })
      expect(pack_factor({ metadata: { packs_per_case: 6, units_per_case: 6 } })).toEqual({ per_case: 6, basis: "unit" })
      expect(pack_factor({ metadata: { units_per_case: 12 } })).toEqual({ per_case: 12, basis: "unit" })
      expect(pack_factor({ metadata: {} })).toEqual({ per_case: 1, basis: "case" })
    })

    it("prints what each case is", () => {
      expect(pack_caption({ metadata: { packs_per_case: 6, units_per_case: 90 } })).toBe("1 case = 6 packs = 90 units")
      expect(pack_caption({ metadata: { units_per_case: 12 } })).toBe("1 case = 12 units, no inner pack")
      expect(pack_caption({ metadata: null })).toBe("No pack size in NetSuite, counted in cases")
    })
  })

  describe("packs_view_of_hierarchy", () => {
    it("counts packs on the leaves, rolls them up, and falls back to cases with no pack size", () => {
      const roots = [
        {
          id: "c",
          name: "Target",
          level: "customer",
          children: [leaf("a", { packs_per_case: 6, units_per_case: 90 }, 2), leaf("b", null, 3)],
          sales_data: [],
        },
      ]
      const out = packs_view_of_hierarchy(roots as any) as any
      expect(out[0].children[0].sales_data[0].metrics.units_sold).toBe(12)
      expect(out[0].children[0].metadata.units_per_case).toBe(6)
      expect(pack_caption(out[0].children[0])).toBe("1 case = 6 packs = 90 units")
      expect(out[0].children[1].sales_data[0].metrics.units_sold).toBe(3)
      expect(out[0].sales_data[0].metrics.units_sold).toBe(15)
    })
  })
}
